// Client-side image compression — biggest win for slow uploads on cellular.
// A raw iPhone photo is 5-10 MB. Compressed to 1920px @ q=0.82 it's ~500 KB
// with no visible loss for inspection/appliance photos. Skips small files
// and non-images (videos pass through untouched).
//
// Usage:
//   import { compressImage } from "@/lib/compress";
//   const smaller = await compressImage(file);   // returns File
//   fd.append("file", smaller);

const MAX_DIM = 1920;
const QUALITY = 0.82;
const SKIP_UNDER_BYTES = 800 * 1024; // don't bother compressing < 800 KB

export async function compressImage(file, opts = {}) {
  if (!file || !(file instanceof Blob)) return file;
  const type = file.type || "";
  // Videos, HEICs the browser can't decode, PDFs → pass through
  if (!type.startsWith("image/")) return file;
  if (type === "image/heic" || type === "image/heif") return file;
  if (file.size < (opts.skipUnder ?? SKIP_UNDER_BYTES)) return file;

  const maxDim = opts.maxDim ?? MAX_DIM;
  const quality = opts.quality ?? QUALITY;

  try {
    const bitmap = await createImageBitmap(file);
    const { width: w, height: h } = bitmap;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    if (scale >= 1) { bitmap.close?.(); return file; } // already small enough
    const nw = Math.round(w * scale);
    const nh = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = nw;
    canvas.height = nh;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, nw, nh);
    bitmap.close?.();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob) return file;
    // Give it a .jpg name so the backend picks the right extension
    const baseName = (file.name || "photo").replace(/\.[^./]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch (_e) {
    // Any decode/canvas error → fall back to original
    return file;
  }
}

// Best-effort auto-classify appliance TYPE + LOCATION from Claude's OCR type string.
// Mirrors _appliance_default_location() on the backend.
const APPLIANCE_LOCATION_RULES = [
  { match: /refrigerator|fridge|freezer/i,               location: "Kitchen" },
  { match: /dishwash/i,                                   location: "Kitchen" },
  { match: /disposal|garbage/i,                           location: "Kitchen" },
  { match: /range hood|hood/i,                            location: "Kitchen" },
  { match: /range|stove|oven|cooktop|microwave/i,         location: "Kitchen" },
  { match: /washer|dryer|laundry/i,                       location: "Laundry Room" },
  { match: /water heater/i,                               location: "Basement" },
  { match: /furnace|hvac|condenser|air handler|ac unit|a\/c/i, location: "Basement" },
  { match: /garage door|opener/i,                         location: "Garage" },
  { match: /smoke|carbon monoxide|co detector/i,          location: "Hallway" },
];

export function locationForApplianceType(t) {
  const s = (t || "").toString();
  for (const r of APPLIANCE_LOCATION_RULES) {
    if (r.match.test(s)) return r.location;
  }
  return "Kitchen";
}

// Normalize Claude's freeform 'type' into a canonical picker option.
const APPLIANCE_TYPE_MAP = [
  { match: /refrigerator|fridge/i,           canonical: "Refrigerator" },
  { match: /freezer/i,                        canonical: "Refrigerator" },
  { match: /dishwash/i,                        canonical: "Dishwasher" },
  { match: /washer/i,                          canonical: "Washer" },
  { match: /dryer/i,                           canonical: "Dryer" },
  { match: /water heater/i,                   canonical: "Water Heater" },
  { match: /furnace/i,                         canonical: "Furnace" },
  { match: /condenser|ac unit|a\/c unit/i,    canonical: "AC Condenser" },
  { match: /air handler|hvac/i,               canonical: "HVAC Air Handler" },
  { match: /disposal|garbage/i,               canonical: "Garbage Disposal" },
  { match: /range hood|hood/i,                canonical: "Range Hood" },
  { match: /range|stove/i,                     canonical: "Range / Stove" },
  { match: /oven/i,                            canonical: "Oven" },
  { match: /cooktop/i,                         canonical: "Cooktop" },
  { match: /microwave/i,                       canonical: "Microwave" },
  { match: /smoke|carbon monoxide|co detector/i, canonical: "Smoke Detector" },
];

export function canonicalApplianceType(t) {
  const s = (t || "").toString();
  for (const r of APPLIANCE_TYPE_MAP) {
    if (r.match.test(s)) return r.canonical;
  }
  return (s || "").trim();
}
